import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

import { SessionManager } from '../../utils/SessionManager';
import {
  getUnreadCount,
  markAsRead,
  markAsUnread,
  markAllAsRead,
  getNotifications,
} from '../../services/notificationService';
import type { Notification, NotificationBellProps, UseNotificationBellReturn } from './types';

/**
 * 通知ベルコンポーネントのロジックを管理するカスタムフック
 */
export const useNotificationBell = ({
  isMobile = false,
  onToggle,
}: NotificationBellProps): UseNotificationBellReturn => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [processingNotificationIds, setProcessingNotificationIds] = useState<number[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMoreNotifications, setHasMoreNotifications] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [bellPosition, setBellPosition] = useState<{
    top: number;
    right: number;
    left: number;
    width: number;
    viewportTop: number;
  }>({ top: 0, right: 0, left: 0, width: 0, viewportTop: 0 });

  const panelRef = useRef<HTMLDivElement>(null);
  const notificationListRef = useRef<HTMLDivElement>(null);
  const bellIconRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const unauthorizedEventHandledRef = useRef<boolean>(false);
  // 処理済み通知IDの追跡用ref（状態更新の即時反映のため）
  const processedNotificationIdsRef = useRef<Set<string>>(new Set());

  // ベルアイコンの位置を取得する関数
  const updateBellPosition = useCallback(() => {
    if (bellIconRef.current) {
      const rect = bellIconRef.current.getBoundingClientRect();
      
      // 画面内に収まるように位置を調整
      const maxHeight = window.innerHeight - 50; // 画面下部の余白
      const panelHeight = Math.min(400, maxHeight); // パネルの最大高さ
      
      // スクロール位置を考慮した表示位置の計算
      let topPosition = rect.bottom;
      
      // 画面下部に隠れないように調整
      if (topPosition + panelHeight > window.innerHeight) {
        // ベルアイコンの上に表示
        topPosition = Math.max(10, rect.top - panelHeight);
      }
      
      setBellPosition({
        top: topPosition,
        right: window.innerWidth - rect.right,
        left: rect.left,
        width: rect.width,
        viewportTop: topPosition  // ビューポート相対の位置も保存
      });
    }
  }, []);

  // パネルが開かれる時にベルの位置を更新
  useEffect(() => {
    if (isOpen) {
      updateBellPosition();
      // パネルが開かれたときにページ状態をリセット
      setCurrentPage(1);
      setHasMoreNotifications(true);
    }
  }, [isOpen, updateBellPosition]);

  // ウィンドウサイズ変更時とスクロール時にも位置を更新
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        updateBellPosition();
      }
    };
    
    const handleScroll = () => {
      if (isOpen) {
        updateBellPosition();
      }
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen, updateBellPosition]);

  // 認証状態の確認
  useEffect(() => {
    const checkAuth = () => {
      const activeSession = SessionManager.getActiveSession();
      const isLoggedIn = !!activeSession && !!activeSession.token;
      setIsAuthenticated(isLoggedIn);
      return isLoggedIn;
    };

    // 初期チェック
    if (checkAuth()) {
      // 認証済みの場合のみ通知数を取得
      fetchUnreadCount().then(count => {
        // 初期状態でカスタムイベントを発行して通知状態を共有
        if (count > 0) {
          const unreadStatusEvent = new CustomEvent('notification-status-changed', {
            detail: { hasUnread: true }
          });
          window.dispatchEvent(unreadStatusEvent);
        }
      });
    }

    // unauthorizedイベントハンドラ関数を作成
    const handleUnauthorized = () => {
      if (!unauthorizedEventHandledRef.current) {
        unauthorizedEventHandledRef.current = true;
        setIsAuthenticated(false);
        setUnreadCount(0);
        setNotifications([]);
        
        // フラグをリセット（次のイベントで再度処理できるように）
        setTimeout(() => {
          unauthorizedEventHandledRef.current = false;
        }, 500);
      }
    };
    
    // ログインイベントハンドラ関数を作成
    const handleLogin = () => {
      const isLoggedIn = checkAuth();
      if (isLoggedIn) {
        fetchUnreadCount();
      }
    };
    
    // イベントリスナーを登録
    window.addEventListener('unauthorized', handleUnauthorized);
    window.addEventListener('login', handleLogin);
    
    // クリーンアップ関数
    return () => {
      window.removeEventListener('unauthorized', handleUnauthorized);
      window.removeEventListener('login', handleLogin);
    };
  }, []);

  // 通知数の定期的な更新
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      const activeSession = SessionManager.getActiveSession();
      if (activeSession && activeSession.token) {
        fetchUnreadCount().then(count => {
          // 未読数に変更があった場合に状態を共有
          if ((count > 0) !== (unreadCount > 0)) {
            const unreadStatusEvent = new CustomEvent('notification-status-changed', {
              detail: { hasUnread: count > 0 }
            });
            window.dispatchEvent(unreadStatusEvent);
          }
        });
      } else {
        // セッションが無効になった場合はインターバルをクリア
        clearInterval(interval);
      }
    }, 60000); // 1分ごとに更新
    
    return () => clearInterval(interval);
  }, [isAuthenticated, unreadCount]);

  // イベントリスナーによるクリックアウト処理
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    // パネルが開いているときだけイベントリスナーを追加
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // パネルが閉じられるときに後処理を行う
  useEffect(() => {
    // パネルが閉じられた時
    if (!isOpen && isMobile) {
      // モバイル版の場合、bodyのスクロールを許可して背景オーバーレイを削除
      document.body.style.overflow = '';
      document.body.classList.remove('notification-panel-open');
      document.getElementById('notification-overlay')?.remove();
    }
  }, [isOpen, isMobile]);

  // コンポーネントがアンマウントされたときに後処理
  useEffect(() => {
    return () => {
      // オーバーレイが残っていれば削除
      document.body.style.overflow = '';
      document.body.classList.remove('notification-panel-open');
      document.getElementById('notification-overlay')?.remove();
    };
  }, []);

  // 未読通知数を取得
  const fetchUnreadCount = async (): Promise<number> => {
    const activeSession = SessionManager.getActiveSession();
    if (!activeSession || !activeSession.token) {
      setIsAuthenticated(false);
      return 0;
    }
    
    try {
      const count = await getUnreadCount();
      setUnreadCount(count);
      return count;
    } catch (error) {
      console.error('未読通知数の取得に失敗しました', error);
      // エラーハンドリングはaxiosインターセプターに任せる
      return 0;
    }
  };

  // 通知データを正規化するユーティリティ関数
  const normalizeNotificationData = (notification: Notification): Notification => {
    // DBではisReadだが、UIではis_readを使っている可能性がある
    const isReadValue = 
      notification.isRead === false || 
      notification.is_read === false ? false : true;
    
    return {
      ...notification,
      // 両方のフィールドに同じ値を設定
      is_read: isReadValue,
      isRead: isReadValue,
    };
  };

  // 通知データを取得する関数
  const fetchNotificationsData = async (): Promise<void> => {
    try {
      setIsLoading(true);
      // 表示時は常にページをリセット
      setCurrentPage(1);
      setHasMoreNotifications(true);
      setNotifications([]);
      
      const response = await getNotifications(1);

      // レスポンス構造の検証
      if (response && response.success) {
        // APIレスポンスから通知データを抽出
        let notificationData: Notification[] = [];
        let totalItems = 0;
        
        // response.dataがオブジェクトでnotificationsプロパティを持つ場合
        if (response.data && Array.isArray(response.data.notifications)) {
          notificationData = response.data.notifications;
          totalItems = response.data.totalCount || response.data.total || 0;
          
          // 総件数から追加データの有無を判断
          if (totalItems > notificationData.length) {
            setHasMoreNotifications(true);
          } else {
            setHasMoreNotifications(false);
          }
        } 
        // その他のケースも処理（安全のため）
        else if (Array.isArray(response.data)) {
          notificationData = response.data;
        }
        
        if (notificationData.length > 0) {
          // 各通知を正規化して更新
          const normalizedData = notificationData.map(normalizeNotificationData);
          setNotifications(normalizedData);
          
          // 未読カウントを計算
          const newUnreadCount = normalizedData.filter(n => !n.is_read).length;
          if (newUnreadCount !== unreadCount) {
            setUnreadCount(newUnreadCount);
            
            // 未読状態の変化を通知
            const hasUnreadChanged = (unreadCount === 0 && newUnreadCount > 0) || 
                                    (unreadCount > 0 && newUnreadCount === 0);
            
            if (hasUnreadChanged) {
              const unreadStatusEvent = new CustomEvent('notification-status-changed', {
                detail: { hasUnread: newUnreadCount > 0 }
              });
              window.dispatchEvent(unreadStatusEvent);
            }
          }
        } else {
          setNotifications([]);
          
          // 未読カウントが0でなければリセット
          if (unreadCount !== 0) {
            setUnreadCount(0);
            const unreadStatusEvent = new CustomEvent('notification-status-changed', {
              detail: { hasUnread: false }
            });
            window.dispatchEvent(unreadStatusEvent);
          }
        }
      } else {
        console.error('APIレスポンスエラー (パネル表示時):', response);
        setNotifications([]);
      }
    } catch (error) {
      console.error('通知の取得に失敗しました (パネル表示時)', error);
      setNotifications([]);
    } finally {
      setIsLoading(false);
    }
  };

  // 通知を既読にする処理
  const handleMarkAsRead = async (notification: Notification, event?: React.SyntheticEvent): Promise<void> => {
    // eventが存在する場合のみイベントメソッドを呼び出す
    if (event) {
      event.preventDefault?.();
      event.stopPropagation?.();
    }
    
    const notificationId = notification.id;

    // 処理フラグをチェック（既に処理中または処理済みならスキップ）
    const processKey = `read-${notificationId}`;
    if (processingNotificationIds.includes(notificationId) ||
        processedNotificationIdsRef.current.has(processKey)) {
      return;
    }
    
    // 処理済みフラグをセット（重複処理防止のため）
    processedNotificationIdsRef.current.add(processKey);
    
    // 処理中IDリストにも追加（UI表示用）
    setProcessingNotificationIds(prev => [...prev, notificationId]);
    
    const activeSession = SessionManager.getActiveSession();
    if (!activeSession || !activeSession.token) {
      // 処理フラグをクリア
      processedNotificationIdsRef.current.delete(processKey);
      setProcessingNotificationIds(prev => prev.filter(id => id !== notificationId));
      
      setIsAuthenticated(false);
      navigate('/login');
      return;
    }
    
    try {
      // 通知を既読にする
      if (!notification.is_read) {
        // UIを先に更新
        setNotifications(prevNotifications => {
          const updatedNotifications = prevNotifications.map(item =>
            item.id === notificationId
              ? { ...item, is_read: true, isRead: true }
              : item
          );
          return updatedNotifications;
        });

        // 未読カウントを事前に更新
        const oldUnreadCount = unreadCount;
        const newUnreadCount = Math.max(0, oldUnreadCount - 1);
        setUnreadCount(newUnreadCount);

        // APIを呼び出して通知を既読に
        const response = await markAsRead(notificationId);
        
        if (!response || !response.success) {
          // APIエラー時は元に戻す
          console.error('既読化APIエラー:', response);
          
          // 通知リストを元に戻す
          setNotifications(prevNotifications => 
            prevNotifications.map(item => 
              item.id === notificationId 
                ? { ...item, is_read: false, isRead: false } 
                : item
            )
          );
          
          // カウントを戻す
          setUnreadCount(oldUnreadCount);
          
          // 状態を戻すイベントを発行（必要な場合）
          if (newUnreadCount === 0) {
            const unreadStatusEvent = new CustomEvent('notification-status-changed', {
              detail: { hasUnread: true }
            });
            window.dispatchEvent(unreadStatusEvent);
          }
          
          alert('通知の既読化に失敗しました。再度お試しください。');
        }
      }
    } catch (error) {
      console.error('通知の既読処理に失敗しました', error);
      
      // エラー状態をUIに反映
      setNotifications(prevNotifications => 
        prevNotifications.map(item => 
          item.id === notificationId 
            ? { ...item, is_read: false, isRead: false } 
            : item
        )
      );
      
      // エラーの詳細情報を表示
      if ((error as any).response) {
        console.error('サーバーレスポンス:', (error as any).response.data);
        alert(`通知の既読化に失敗しました: ${(error as any).response.data?.message || 'サーバーエラー'}`);
      } else {
        alert('通知の既読化に失敗しました。再度お試しください。');
      }
    } finally {
      // 処理中リストから削除
      setProcessingNotificationIds(prev => prev.filter(id => id !== notificationId));
      
      // 一定時間後に処理済みフラグをクリア（次の操作を許可）
      setTimeout(() => {
        processedNotificationIdsRef.current.delete(processKey);
      }, 800);
    }
  };

  // 通知を未読に戻す処理
  const handleMarkAsUnread = async (notification: Notification, event?: React.SyntheticEvent): Promise<void> => {
    // eventが存在する場合のみイベントメソッドを呼び出す
    if (event) {
      event.preventDefault?.();
      event.stopPropagation?.();
    }
    
    const notificationId = notification.id;

    // 処理フラグをチェック（既に処理中または処理済みならスキップ）
    const processKey = `unread-${notificationId}`;
    if (processingNotificationIds.includes(notificationId) ||
        processedNotificationIdsRef.current.has(processKey)) {
      return;
    }
    
    // 処理済みフラグをセット
    processedNotificationIdsRef.current.add(processKey);
    
    // 処理中IDリストにも追加（UI表示用）
    setProcessingNotificationIds(prev => [...prev, notificationId]);
    
    const activeSession = SessionManager.getActiveSession();
    if (!activeSession || !activeSession.token) {
      // 処理フラグをクリア
      processedNotificationIdsRef.current.delete(processKey);
      setProcessingNotificationIds(prev => prev.filter(id => id !== notificationId));
      
      setIsAuthenticated(false);
      navigate('/login');
      return;
    }
    
    try {
      // 通知を未読に戻す
      if (notification.is_read) {
        // UIを先に更新
        setNotifications(prevNotifications => {
          const updatedNotifications = prevNotifications.map(item =>
            item.id === notificationId
              ? { ...item, is_read: false, isRead: false }
              : item
          );
          return updatedNotifications;
        });
        
        // 未読カウントを事前に更新
        const newUnreadCount = unreadCount + 1;
        setUnreadCount(newUnreadCount);
        
        // 未読状態がゼロから増えた場合はイベントを発行
        if (unreadCount === 0) {
          const unreadStatusEvent = new CustomEvent('notification-status-changed', {
            detail: { hasUnread: true }
          });
          window.dispatchEvent(unreadStatusEvent);
        }
        
        // APIを呼び出して通知を未読に戻す
        const response = await markAsUnread(notificationId);
        
        if (!response || !response.success) {
          // APIエラー時は元に戻す
          console.error('未読化APIエラー:', response);
          // 通知リストを戻す
          setNotifications(prevNotifications => {
            return prevNotifications.map(item => 
              item.id === notificationId 
                ? { ...item, is_read: true, isRead: true } 
                : item
            );
          });
          // カウントを戻す
          setUnreadCount(unreadCount);
          
          alert('通知を未読に戻すことができませんでした。再度お試しください。');
        }
      }
    } catch (error) {
      console.error('通知の未読処理に失敗しました', error);
      
      // エラー発生時はUIを元に戻す
      setNotifications(prevNotifications => {
        return prevNotifications.map(item => 
          item.id === notificationId 
            ? { ...item, is_read: true, isRead: true } 
            : item
        );
      });
      setUnreadCount(Math.max(0, unreadCount - 1));
      
      // より詳細なエラーメッセージ
      if ((error as any).response) {
        console.error('サーバーレスポンス:', (error as any).response.data);
        alert(`通知を未読に戻すことができませんでした: ${(error as any).response.data?.message || 'サーバーエラー'}`);
      } else if ((error as any).request) {
        alert('サーバーに接続できませんでした。ネットワーク接続を確認してください。');
      } else {
        alert('通知を未読に戻すことができませんでした。再度お試しください。');
      }
    } finally {
      // 処理中リストから削除
      setProcessingNotificationIds(prev => prev.filter(id => id !== notificationId));
      
      // 一定時間後に処理済みフラグをクリア
      setTimeout(() => {
        processedNotificationIdsRef.current.delete(processKey);
      }, 800);
    }
  };

  // すべての通知を既読にする
  const handleMarkAllAsRead = async (): Promise<void> => {
    const activeSession = SessionManager.getActiveSession();
    if (!activeSession || !activeSession.token) {
      setIsAuthenticated(false);
      return;
    }
    
    try {
      const response = await markAllAsRead();

      if (response && response.success) {
        // 通知リストを更新
        setNotifications(prevNotifications => 
          prevNotifications.map(item => ({ ...item, is_read: true, isRead: true }))
        );
        
        // 未読カウントが0より大きい場合のみイベントを発行
        if (unreadCount > 0) {
          setUnreadCount(0);
          
          // 未読状態が変わる場合はイベントを発行
          const unreadStatusEvent = new CustomEvent('notification-status-changed', {
            detail: { hasUnread: false }
          });
          window.dispatchEvent(unreadStatusEvent);
        }
      } else {
        console.error('全既読化APIエラー:', response);
        alert('すべての通知を既読にできませんでした。再度お試しください。');
      }
    } catch (error) {
      console.error('すべての通知を既読にできませんでした', error);
      alert('すべての通知を既読にできませんでした。再度お試しください。');
    }
  };

  // 通知パネルの開閉を切り替える
  const toggleNotificationPanel = useCallback((): void => {
    const activeSession = SessionManager.getActiveSession();
    if (!activeSession || !activeSession.token) {
      setIsAuthenticated(false);
      navigate('/login');
      return;
    }
    
    // パネル開閉を切り替え
    const newIsOpen = !isOpen;
    
    if (newIsOpen) {
      // 開く前に位置を更新
      updateBellPosition();
    }
    
    setIsOpen(newIsOpen);
    
    // onToggleコールバック呼び出し
    if (typeof onToggle === 'function') {
      onToggle(newIsOpen);
    }
    
    // bodyに通知パネル表示中のクラスを追加/削除
    if (newIsOpen) {
      document.body.classList.add('notification-panel-open');
    } else {
      document.body.classList.remove('notification-panel-open');
    }
    
    // モバイル版の場合、bodyのスクロールを制御
    if (isMobile) {
      if (newIsOpen) {
        // パネルを開くときはスクロール禁止
        document.body.style.overflow = 'hidden';
        // オーバーレイを表示
        const overlay = document.createElement('div');
        overlay.className = 'mobile-notification-overlay visible';
        overlay.id = 'notification-overlay';
        overlay.style.zIndex = '9999998';
        overlay.addEventListener('click', () => {
          setIsOpen(false);
          document.body.style.overflow = '';
          document.body.classList.remove('notification-panel-open');
          document.getElementById('notification-overlay')?.remove();
        });
        document.body.appendChild(overlay);
      } else {
        // パネルを閉じるときはスクロール許可
        document.body.style.overflow = '';
        // オーバーレイを削除
        document.getElementById('notification-overlay')?.remove();
      }
    }
    
    // パネルを開く場合は最新の通知データを取得する
    if (newIsOpen) {
      // パネル表示時に通知データを取得
      fetchNotificationsData();
    }
  }, [isOpen, isMobile, navigate, onToggle, updateBellPosition]);

  // 日時のフォーマット
  const formatDate = useCallback((dateString: string): string => {
    const date = new Date(dateString);
    return format(date, 'M月d日 HH:mm', { locale: ja });
  }, []);

  // 通知をクリックして移動する処理
  const handleNavigate = async (notification: Notification, event?: React.SyntheticEvent): Promise<void> => {
    // eventが存在する場合のみイベントメソッドを呼び出す
    if (event) {
      event.stopPropagation?.(); // イベントの伝播を停止
      event.preventDefault?.(); // デフォルトの挙動を停止
    }
    
    // 処理中の場合は移動しない
    if (processingNotificationIds.includes(notification.id)) {
      return;
    }

    // 通知の種類に応じて適切なページに遷移
    const { reference_type, reference_id } = notification;
    
    // video_idの取得（異なる形式に対応）
    const videoId = notification.video_id || 
                   (notification.video && (notification.video as any).id) || 
                   (reference_type === 'video' ? reference_id : null);
    
    if (videoId) {
      switch (reference_type) {
        case 'video':
          navigate(`/videos/${videoId}`);
          break;
        case 'comment':
        case 'child_comment':
          // コメントの場合は動画ページに遷移し、コメント位置までスクロール
          navigate(`/videos/${videoId}?comment=${reference_id}`);
          break;
        default:
          // その他の種類でも動画IDがあれば動画ページに遷移
          navigate(`/videos/${videoId}`);
          break;
      }
    } else {
      alert('この通知に関連する動画がありません。');
    }
    
    setIsOpen(false);
    
    // 既読にする処理も実行
    if (!notification.is_read) {
      // モックイベントオブジェクトを作成し、handleMarkAsReadに渡す
      await handleMarkAsRead(notification, { 
        preventDefault: () => {}, 
        stopPropagation: () => {} 
      } as React.SyntheticEvent);
    }
  };

  return {
    isOpen,
    unreadCount,
    notifications,
    isLoading,
    processingNotificationIds,
    isAuthenticated,
    panelRef: panelRef as React.RefObject<HTMLDivElement>,
    notificationListRef: notificationListRef as React.RefObject<HTMLDivElement>,
    bellIconRef: bellIconRef as React.RefObject<HTMLDivElement>,
    bellPosition,
    toggleNotificationPanel,
    handleMarkAsRead,
    handleMarkAsUnread,
    handleMarkAllAsRead,
    handleNavigate,
    formatDate
  };
}; 